import { useContext, useEffect, useState, useCallback } from 'react';
import { Button } from '@mui/material';
import { Context } from '../../AuthContext';

const staticAccounts = {
  onedrive: { text: 'OneDrive', icon: '/workbench/onedrive.svg', status: 'coming-soon' },
  //   slack: { text: 'Slack', icon: '/workbench/slack.svg', status: 'coming-soon' },
  notion: { text: 'Notion', icon: '/workbench/notion.svg', status: 'coming-soon' },
  trello: { text: 'Trello', icon: '/workbench/trello.svg', status: 'coming-soon' },
  orcid: { text: 'Orcid', icon: '/workbench/orcid.svg', status: 'coming-soon' },
};

const ConnectedAccounts = () => {
  const { user } = useContext(Context);
  const [googleStatus, setGoogleStatus] = useState('loading'); // loading | not-linked | linked
  const [googleEmail, setGoogleEmail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}oauth/google/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setGoogleStatus('not-linked');
        return;
      }
      const data = await res.json();
      setGoogleStatus(data.connected ? 'linked' : 'not-linked');
      setGoogleEmail(data.email || null);
    } catch (e) {
      setGoogleStatus('not-linked');
    }
  }, [user]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get('google');
    if (g === 'connected') {
      setBanner({ kind: 'success', text: 'Google Drive connected.' });
    } else if (g === 'error') {
      setBanner({
        kind: 'error',
        text: `Could not connect Google Drive${
          params.get('message') ? `: ${params.get('message')}` : ''
        }.`,
      });
    }
    if (g) {
      params.delete('google');
      params.delete('message');
      const qs = params.toString();
      const newUrl = window.location.pathname + (qs ? `?${qs}` : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const linkGoogle = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}oauth/google/start`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setBanner({ kind: 'error', text: 'Could not start Google sign-in.' });
        setBusy(false);
      }
    } catch (e) {
      setBanner({ kind: 'error', text: 'Could not start Google sign-in.' });
      setBusy(false);
    }
  };

  const unlinkGoogle = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}oauth/google/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setGoogleStatus('not-linked');
      setGoogleEmail(null);
      setBanner({ kind: 'success', text: 'Google Drive disconnected.' });
    } catch (e) {
      setBanner({ kind: 'error', text: 'Could not disconnect Google Drive.' });
    } finally {
      setBusy(false);
    }
  };

  const renderRow = ({ key, text, icon, status, email, onLink, onUnlink, linkLabel }) => (
    <div
      key={key}
      style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '24px' }}
    >
      {icon && <img src={icon} alt={text} style={{ width: '48px', height: '48px' }} />}
      <div style={{ width: '180px', display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontWeight: 700 }}>{text}</span>
        {status === 'linked' && email && (
          <span style={{ color: '#475467', fontSize: 12 }}>{email}</span>
        )}
      </div>
      <div style={{ width: '180px' }}>
        {status === 'coming-soon' && (
          <Button variant="contained" disabled fullWidth>
            Coming Soon ...
          </Button>
        )}
        {status === 'loading' && (
          <Button variant="contained" disabled fullWidth>
            Loading…
          </Button>
        )}
        {status === 'not-linked' && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={busy || !onLink}
            onClick={onLink}
          >
            {linkLabel || 'Link Account'}
          </Button>
        )}
        {status === 'linked' && (
          <Button
            variant="contained"
            color="error"
            fullWidth
            disabled={busy || !onUnlink}
            onClick={onUnlink}
          >
            Unlink Account
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ textAlign: 'left' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <h2>Connected Accounts</h2>
        <p
          style={{
            width: '40%',
            marginRight: '220px',
            fontSize: '12px',
            lineHeight: '1.2',
          }}
        >
          Connect external apps securely to extend your workflow within TSP. TSP only requests the
          permissions needed for the features you choose to use, never unrelated files, emails, or
          personal account data. You can disconnect access at any time without deleting existing
          work in TSP.
        </p>
      </div>
      <p>
        Connect your external tools to your TSP account here. You'll be asked to securely grant
        permission for each tool to enable full functionality.
      </p>
      {banner && (
        <div
          style={{
            padding: '10px 12px',
            marginBottom: 16,
            borderRadius: 6,
            width: '80%',
            background: banner.kind === 'success' ? '#ECFDF3' : '#FEF3F2',
            color: banner.kind === 'success' ? '#027A48' : '#B42318',
            border: `1px solid ${banner.kind === 'success' ? '#ABEFC6' : '#FECDCA'}`,
          }}
        >
          {banner.text}
        </div>
      )}
      <div>
        {renderRow({
          key: 'google',
          text: 'Google',
          icon: '/workbench/google.svg',
          status: googleStatus,
          email: googleEmail,
          onLink: linkGoogle,
          onUnlink: unlinkGoogle,
        })}
        {Object.entries(staticAccounts).map(([key, account]) => renderRow({ key, ...account }))}
      </div>
    </div>
  );
};
export default ConnectedAccounts;
